import React, { useEffect, useMemo, useState } from 'react';
import { FilterOptions, SurveyData } from '../types';
import { getWorkshopName, getPartnerName } from '../utils/analytics';
import { ChevronUp, ChevronDown, Search, Download, ArrowLeft, LogOut } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Footer from './Footer';
import apiClient from '../utils/api';
import LoadingOverlay from './LoadingOverlay';
import { PortalLogoLink } from './PortalLogoLink';

interface ResponsesPageProps {
    filters: FilterOptions;
    onLogout: () => void;
}

const ResponsesPage: React.FC<ResponsesPageProps> = ({ filters, onLogout }) => {
    const navigate = useNavigate();
    const [data, setData] = useState<SurveyData[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortField, setSortField] = useState<keyof SurveyData>('responseTimestamp');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 20; // Increased for better viewing on dedicated page

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        (async () => {
            try {
                const rows = await apiClient.getSurveyResponses({
                    startDate: filters.dateRange.start || undefined,
                    endDate: filters.dateRange.end || undefined,
                    workshopId: filters.workshops.length ? filters.workshops.join(',') : undefined,
                    district: filters.districts.length ? filters.districts.join(',') : undefined,
                    implementationPartnerId: filters.implementationPartners.length ? filters.implementationPartners.join(',') : undefined,
                });
                if (!cancelled) setData(Array.isArray(rows) ? rows : []);
            } catch {
                if (!cancelled) setData([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [filters]);

    const filteredAndSortedData = useMemo(() => {
        let filtered = data.filter(item =>
            item.candidateName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.district.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.batchId.toLowerCase().includes(searchTerm.toLowerCase())
        );

        filtered.sort((a, b) => {
            let aValue: any = a[sortField];
            let bValue: any = b[sortField];

            if (sortField === 'response') {
                aValue = a.response.rating;
                bValue = b.response.rating;
            }

            if (typeof aValue === 'string') {
                aValue = aValue.toLowerCase();
                bValue = bValue.toLowerCase();
            }

            if (sortDirection === 'asc') {
                return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
            } else {
                return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
            }
        });

        return filtered;
    }, [data, searchTerm, sortField, sortDirection]);

    const paginatedData = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        return filteredAndSortedData.slice(startIndex, startIndex + itemsPerPage);
    }, [filteredAndSortedData, currentPage]);

    const totalPages = Math.ceil(filteredAndSortedData.length / itemsPerPage);

    const handleSort = (field: keyof SurveyData) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const exportToCSV = () => {
        const headers = [
            'Candidate Name', 'Mobile', 'District', 'Workshop', 'Implementation Partner',
            'Batch ID', 'Start Date', 'End Date', 'Response Date',
            'Q1 (Content Easy)', 'Q2 (Added Value)', 'Q3 (Interaction)',
            'Q4 (Arrangements)', 'Q5 (Recommend)', 'Rating'
        ];

        const csvContent = [
            headers.join(','),
            ...data.map(item => [
                `"${item.candidateName}"`,
                item.candidateMobile,
                `"${item.district}"`,
                `"${getWorkshopName(item.workshopId)}"`,
                `"${getPartnerName(item.implementationPartnerId)}"`,
                item.batchId,
                item.workshopStartDate,
                item.workshopEndDate,
                item.responseTimestamp,
                item.response.q1,
                item.response.q2,
                item.response.q3,
                item.response.q4,
                item.response.q5,
                item.response.rating
            ].join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'msme-survey-data.csv';
        a.click();
        window.URL.revokeObjectURL(url);
    };

    const SortIcon = ({ field }: { field: keyof SurveyData }) => {
        if (sortField !== field) return null;
        return sortDirection === 'asc' ?
            <ChevronUp className="w-4 h-4" /> :
            <ChevronDown className="w-4 h-4" />;
    };

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col" aria-busy={loading}>
            <LoadingOverlay show={loading} label="Loading responses…" />
            {/* Header */}
            <div className="bg-white shadow-sm border-b border-gray-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between py-6">
                        <div className="flex items-center space-x-4">
                            <button
                                onClick={() => navigate('/feedback-dashboard')}
                                className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
                            >
                                <ArrowLeft className="w-5 h-5 mr-2" />
                                Back to Feedback Dashboard
                            </button>
                            <div className="flex items-center gap-3">
                                <PortalLogoLink imgClassName="h-10 w-auto" />
                                <div>
                                    <h1 className="text-2xl font-bold text-gray-900">
                                        Survey Responses
                                    </h1>
                                    <p className="text-sm text-gray-500">
                                        Detailed view of survey responses {loading ? '(loading...)' : ''}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center space-x-4">
                            <div className="text-sm text-gray-600">
                                Total: {data.length.toLocaleString()} responses
                            </div>
                            <button
                                onClick={exportToCSV}
                                disabled={loading}
                                className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 text-sm"
                            >
                                <Download className="w-4 h-4 mr-2" />
                                {loading ? 'Loading…' : 'Export CSV'}
                            </button>
                            <button
                                onClick={onLogout}
                                className="flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm"
                            >
                                <LogOut className="w-4 h-4 mr-2" />
                                Logout
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                    {/* Search and Controls */}
                    <div className="px-6 py-4 border-b border-gray-200">
                        <div className="flex items-center justify-between">
                            <div className="relative">
                                <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Search responses..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                />
                            </div>
                            <div className="text-sm text-gray-700">
                                Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredAndSortedData.length)} of {filteredAndSortedData.length} results
                            </div>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                        onClick={() => handleSort('candidateName')}
                                    >
                                        <div className="flex items-center">
                                            Candidate
                                            <SortIcon field="candidateName" />
                                        </div>
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Mobile
                                    </th>
                                    <th
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                        onClick={() => handleSort('district')}
                                    >
                                        <div className="flex items-center">
                                            District
                                            <SortIcon field="district" />
                                        </div>
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Workshop
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Partner
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Batch ID
                                    </th>
                                    <th
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                        onClick={() => handleSort('response')}
                                    >
                                        <div className="flex items-center">
                                            Rating
                                            <SortIcon field="response" />
                                        </div>
                                    </th>
                                    <th
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                        onClick={() => handleSort('responseTimestamp')}
                                    >
                                        <div className="flex items-center">
                                            Response Date
                                            <SortIcon field="responseTimestamp" />
                                        </div>
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {paginatedData.map((item, index) => (
                                    <tr key={index} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                            {item.candidateName}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {item.candidateMobile}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {item.district}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            <div className="max-w-xs truncate" title={getWorkshopName(item.workshopId)}>
                                                {getWorkshopName(item.workshopId)}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            <div className="max-w-xs truncate" title={getPartnerName(item.implementationPartnerId)}>
                                                {getPartnerName(item.implementationPartnerId)}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {item.batchId}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            <div className="flex items-center">
                                                <span className="text-yellow-500 mr-1">★</span>
                                                {item.response.rating}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {new Date(item.responseTimestamp).toLocaleDateString()}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div className="px-6 py-4 border-t border-gray-200">
                        <div className="flex items-center justify-between">
                            <div className="text-sm text-gray-700">
                                Page {currentPage} of {totalPages}
                            </div>
                            <div className="flex items-center space-x-2">
                                <button
                                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                                    disabled={currentPage === 1}
                                    className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                                >
                                    Previous
                                </button>
                                <button
                                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                                    disabled={currentPage === totalPages}
                                    className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <Footer />
        </div>
    );
};

export default ResponsesPage;



